import {
  Alert,
  Box,
  // Button,
  // Container,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Popover,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { $APP_NAME, ROLES } from "src/constants";
import BalanceCard from "./components/BalanceCard";
import ChargeAmountCards from "./components/ChargeAmountCards";
import {
  Wallet,
  AccountBalance as AccountBalanceIcon,
  // Receipt,
  MoreVert,
  ContentCopy,
  OpenInNew,
} from "@mui/icons-material";
import DataTable from "src/components/common/DataTables/DataTable";
import moment from "moment";
import { useEffect, useState } from "react";
import {
  useLazyGetWalletChargingStatusQuery,
  useLazyGetWalletTransactionsQuery,
} from "src/_api/mapitShipApi";
import { isNumber, omitBy } from "lodash";
import { useSocket } from "src/_contexts/socket";
import TransactionsFilter from "./components/TransactionsFilter";
import { useSearchParams } from "react-router-dom";
import NationalDayOffer from "./components/NationalDayOffer";
import CopyPopover from "src/modules/Admin/PaymentGatewayTransactions/components/CopyPopover";
import copy from "copy-to-clipboard";
import { useSelector } from "react-redux";
const $COLUMN_WIDTH = 200;

export default function AccountBalance() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { profile } = useSelector((state) => state.entities.profile);
  // console.log( profile?.merchant?.deliveryMethod?.includes("MAPIT_SHIP"));
  const deliveryMethods = profile?.merchant?.deliveryMethod ?? [];

  //======= Wallet Transactions =========
  const [params, setParams] = useState({ page: 1, limit: 20 });

  const handleChange = ({ target: { name, value } }) => {
    setParams((prev) => ({ ...prev, [name]: value }));
  };

  const [
    getWalletBalance,
    { isLoading: transactionsLoading, isFetching: isFetchingTransactions },
  ] = useLazyGetWalletTransactionsQuery();

  const [transactionsData, setTransactionsData] = useState(null);
  const [rowCount, setRowCount] = useState(0);

  const getWalletBalanceHandler = async (params) => {
    try {
      const data = await getWalletBalance(omitBy(params, (v) => !v)).unwrap();
      setTransactionsData(data);
    } catch (error) {
      console.log("Error fetching wallet transactions:", error);
    }
  };

  useEffect(() => {
    getWalletBalanceHandler(params);
  }, []);

  useEffect(() => {
    if (!!transactionsData)
      return setRowCount(transactionsData?.transactions?.totalDocs);
  }, [transactionsData]);

  const pageChangeHandler = (page) => {
    handleChange({ target: { name: "page", value: page } });
    getWalletBalanceHandler({ ...params, page });
  };

  const IDCell = ({ value }) => (
    <Typography variant="caption" sx={{ fontWeight: "bold" }}>
      {value ?? "-"}
    </Typography>
  );

  const ShipmentColumnCell = ({ value }) => {
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);

    const handleOpenMenu = (event) => {
      event.stopPropagation();
      setMenuAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
      setMenuAnchorEl(null);
    };

    const copyTrackingNumber = (event) => {
      event.stopPropagation();
      if (value?.trackingNumber) {
        copy(value?.trackingNumber);
        handleCloseMenu();
      }
    };

    const openTrackingUrl = (event) => {
      event.stopPropagation();
      handleCloseMenu();
      if (value?.trackingUrl) {
        window.open(value.trackingUrl, "_blank", "noreferrer");
      }
    };

    if (!value?.trackingNumber) {
      return null;
    }

    return (
      <Box display="flex" alignItems="center" gap={1}>
        {value?.trackingNumber}
        <IconButton size="small" onClick={handleOpenMenu}>
          <MoreVert />
        </IconButton>
        <Popover
          disableScrollLock={true}
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleCloseMenu}
          onClick={(event) => event.stopPropagation()}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          sx={{
            "& .MuiPaper-root": {
              boxShadow: theme.shadows[2],
              minWidth: 100,
              borderRadius: 1,
            },
          }}
        >
          <List dense sx={{ py: 0 }}>
            <ListItem
              onClick={copyTrackingNumber}
              sx={{
                cursor: "pointer",
                py: 1,
                px: 2,
                "&:hover": { backgroundColor: theme.palette.action.hover },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <ContentCopy fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t("Copy")}
                primaryTypographyProps={{ fontSize: 14 }}
              />
            </ListItem>
            <ListItem
              onClick={openTrackingUrl}
              sx={{
                cursor: "pointer",
                py: 1,
                px: 2,
                "&:hover": { backgroundColor: theme.palette.action.hover },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <OpenInNew fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t("Tranking")}
                primaryTypographyProps={{ fontSize: 14 }}
              />
            </ListItem>
          </List>
        </Popover>
      </Box>
    );
  };

  let columns = [
    {
      field: "shipment",
      renderCell: (params) => <ShipmentColumnCell {...params} />,
      sortable: false,
      headerName: t("Shipment Number"),
      width: $COLUMN_WIDTH * 1,
    },

    {
      field: "Copy",
      headerName: t("Copy"),
      sortable: false,
      width: $COLUMN_WIDTH * 0.4,
      renderCell: ({ row }) => (
        <CopyPopover
          items={[
            {
              label: t("Transaction number"),
              value: row?.transactionNumber,
            },
            ...(row?.shipment?.order?.orderNumber
              ? [
                {
                  label: t("Order No."),
                  value: row?.shipment?.order?.orderNumber,
                },
              ]
              : []),
            ...(row?.shipment?.order?.merchantOrderNumber
              ? [
                {
                  label: t("Order reference number"),
                  value: row?.shipment?.order?.merchantOrderNumber,
                },
              ]
              : []),
          ]}
        />
      ),
    },
    // {
    //   field: "transactionNumber",
    //   renderCell: IDCell,
    //   sortable: false,
    //   headerName: t("Transaction number"),
    //   width: $COLUMN_WIDTH * 1,
    // },
    // {
    //   field: "orderNumber",
    //   valueGetter: ({ row }) => row?.shipment?.order?.orderNumber,
    //   sortable: false,
    //   headerName: t("Order No."),
    //   width: $COLUMN_WIDTH * 1,
    // },
    ...(deliveryMethods.length === 1 && deliveryMethods.includes("MAPIT_SHIP")
      ? []
      : [
        {
          field: "task",
          valueGetter: ({ value }) => value?.taskNumber,
          sortable: false,
          headerName: t("Task No."),
          width: $COLUMN_WIDTH * 1,
        },
      ]),

    {
      field: "taxInvoice",
      headerName: t("Tax Invoice"),
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const { taxInvoice } = params.row;

        if (!taxInvoice) {
          return "—";
        }

        const invoiceUrl = `https://storage.googleapis.com/mapit-staging/${taxInvoice}`;

        return (
          <a
            href={invoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              textDecoration: "underline",
              color: "#1976d2",
              cursor: "pointer",
            }}
          >
            {t("Download")}
          </a>
        );
      },
    },
    {
      field: "deliveryOptionName",
      headerName: t("Company Name"),
      width: $COLUMN_WIDTH * 0.8,
      sortable: false,
      renderCell: ({ row }) => {
        const deliveryOption = row?.shipment?.deliveryOption;

        return (
          <Box display="flex" alignItems="center" gap={1}>
            {deliveryOption?.logo && (
              <Box
                component="img"
                src={deliveryOption.logo}
                alt={deliveryOption.deliveryOptionName}
                sx={{
                  width: 28,
                  height: 28,
                  objectFit: "contain",
                  borderRadius: 2,
                }}
              />
            )}
            <Tooltip title={deliveryOption?.deliveryOptionName} placement="top">
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {deliveryOption?.deliveryOptionName || "-"}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },

    {
      field: "currentStatus",
      renderCell: ({ value }) => (
        <>
          <Tooltip title={t(`transactionStatus.${value}`)} placement="top">
            <Typography variant="body2">
              {t(`transactionStatus.${value}`)}
            </Typography>
          </Tooltip>
        </>
      ),
      sortable: false,
      headerName: t("Status"),
      width: $COLUMN_WIDTH * 1,
    },
    {
      field: "category",
      sortable: false,
      renderCell: ({ value }) => (
        <>
          {!!value ? (
            <Tooltip
              title={t(`transactionsCategories.${value}`)}
              placement="top"
            >
              <Typography variant="body2">
                {t(`transactionsCategories.${value}`)}
              </Typography>
            </Tooltip>
          ) : (
            ""
          )}
        </>
      ),
      headerName: t("Category"),
      width: $COLUMN_WIDTH * 1,
    },
    {
      field: "amount",
      renderCell: ({ row, value }) => {
        if (row.type === "CREDIT") {
          return (
            <bdi>
              <Typography
                variant="subtitle2"
                color="success.main"
                sx={{ fontWeight: "bold" }}
              >
                {"+" + value?.toFixed(2)}
              </Typography>
            </bdi>
          );
        } else if (row.type === "DEBIT") {
          return (
            <bdi>
              <Typography
                variant="subtitle2"
                color="error.main"
                sx={{ fontWeight: "bold" }}
              >
                {"-" + value?.toFixed(2)}
              </Typography>
            </bdi>
          );
        } else {
          <></>;
        }
      },
      sortable: false,
      headerName: t("Amount"),
      width: $COLUMN_WIDTH * 0.6,
    },
    // { field: "feesAmount", sortable: false, valueGetter: ({ value }) => value?.toFixed(2), headerName: t("Tax (15%)"), width: $COLUMN_WIDTH * 0.6, },
    {
      field: "amountWithoutFees",
      sortable: false,
      valueGetter: ({ value, row }) =>
        !!value && row.category === "WALLET_CHARGE" ? value?.toFixed(2) : "",
      headerName: t("Paid price"),
      width: $COLUMN_WIDTH * 0.6,
    },
    {
      field: "currentBalance",
      valueGetter: ({ value }) => value?.toFixed(2),
      sortable: false,
      headerName: t("Remaining amount"),
      width: $COLUMN_WIDTH * 0.7,
    },

    // {
    //     field: "invoiceURL",
    //     renderCell: ({ value, row }) => {
    //         if ((row?.currentStatus === "PENDING" && !!value)) {
    //             return <IconButton
    //                 size="small"
    //                 onClick={() => {
    //                     window.open(value, "_blank", "noreferrer")
    //                 }}>
    //                 <Receipt />
    //             </IconButton>
    //         }
    //         return <></>
    //     },
    //     sortable: false,
    //     headerName: t("Invoice URL"),
    //     width: $COLUMN_WIDTH * 0.8
    // },
    {
      field: "description",
      renderCell: ({ value }) => (
        <Tooltip placement="top" title={value}>
          <Typography
            variant="subtitle2"
            sx={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {value}
          </Typography>
        </Tooltip>
      ),
      sortable: false,
      headerName: t("Note"),
      width: $COLUMN_WIDTH * 1.7,
    },
    {
      field: "createdAt",
      valueGetter: ({ value }) => moment(value).format("DD/MM/YY - h:mm A"),
      sortable: false,
      headerName: t("Created At"),
      width: $COLUMN_WIDTH * 0.8,
    },
  ];

  //======= Wallet Balance =========
  // const { data: balance, isLoading: balanceLoading } = useGetWalletBalanceQuery({})
  const { socket } = useSocket();

  useEffect(
    (_) => {
      if (socket) {
        socket.on("balanceAdded", function (data) {
          getWalletBalance();
        });
      }
    },
    [socket],
  );

  //======= Payment Charge Status =======
  const [
    getkWalletChargingStatus,
    { isLoading: WalletChargingLoading, isFetching: isFetchingWalletCharging },
  ] = useLazyGetWalletChargingStatusQuery();
  const isLoading = WalletChargingLoading || transactionsLoading;
  const isFetching = isFetchingWalletCharging || isFetchingTransactions;
  const [searchParams, setSearchParams] = useSearchParams();
  const transactionNumber = searchParams.get("transactionNumber");

  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);

  const getkWalletChargingStatusHandler = async () => {
    try {
      let response = await getkWalletChargingStatus(transactionNumber).unwrap();
      if (response?.currentStatus === "PAID") {
        setIsSuccess(true);
      } else {
        setIsError(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (!!transactionNumber) {
      getkWalletChargingStatusHandler();
    }
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete("transactionNumber");
      return newParams;
    });
  }, [transactionNumber]);

  return (
    <>
      <Helmet>
        <title>Wallet Balance | {$APP_NAME}</title>
      </Helmet>
      <Typography variant="h2">{t("Wallet Balance")}</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={5} md={5} lg={4} xl={4}>
          <BalanceCard
            Icon={Wallet}
            title={t("Current Balance")}
            value={
              isNumber(transactionsData?.balance)
                ? `${transactionsData?.balance?.toFixed(2)} ${t("Currency")}`
                : ""
            }
            loading={transactionsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={7} md={7} lg={8} xl={8}>
          <NationalDayOffer />
        </Grid>

        {/* <Grid item xs={12} sm={6} md={6} lg={5} xl={4}>
                        <BalanceCard
                            Icon={AccountBalanceIcon}
                            title={t("Amount Withdrawn")}
                            value={isNumber(transactionsData?.amountWithdrawn) ? `${transactionsData?.amountWithdrawn?.toFixed(2)} ${t("Currency")}` : ''}
                            loading={transactionsLoading}
                        />
                    </Grid> */}
      </Grid>
      <Grid item xs={12}>
        <Box my={2}>
          {isSuccess && (
            <Alert severity="success">
              {t("Payment has been completed successfully.")}
            </Alert>
          )}
          {isError && (
            <Alert severity="error">{t("Payment has been failed.")}</Alert>
          )}
        </Box>
      </Grid>
      <ChargeAmountCards />
      <TransactionsFilter
        mt={5}
        params={params}
        setParams={setParams}
        handleChange={handleChange}
        rowCount={rowCount}
        disableExcelSend={!transactionsData?.transactions?.docs?.length}
        onSubmit={() => {
          setParams((prev) => ({ ...prev, page: 1 }));
          getWalletBalanceHandler({ ...params, page: 1 });
        }}
        isLoading={isLoading || isFetching}
      />
      <Paper sx={{ height: "80vh", mt: 1 }}>
        <DataTable
          rows={transactionsData?.transactions?.docs ?? []}
          columns={columns}
          loading={isLoading || isFetching}
          rowCount={rowCount}
          page={params?.page}
          pageSize={20}
          onRowClick={() => { }}
          onPageChange={pageChangeHandler}
          checkboxSelection={false}
          customComponents={["Pagination"]}
        />
      </Paper>
    </>
  );
}
